// Modal hiển thị QR + secret_base32 + INPUT XÁC NHẬN MÃ 6 SỐ.
// Dùng cho:
//  - AC-AUTH-2: Admin gen L1 cho user → admin scan Authenticator → nhập mã 6 số để commit
//  - AC-AUTH-5: Admin reset L2 cho NV → NV scan → admin nhập mã 6 số (NV đọc) để commit
//
// Flow 2-step BẮT BUỘC: nếu admin đóng modal chưa verify, server KHÔNG ghi DB —
// secret L1/L2 cũ vẫn còn, user cũ vẫn login được. Modal sẽ cảnh báo khi đóng.
import { useEffect, useRef, useState } from 'react';
import { showConfirm } from '../lib/dialogs.js';

export default function QrModal({
  open, onClose, title, note,
  qrPng, secretBase32, otpauthUri,
  warningTone = 'info',
  onVerify,           // async (code: string) => void | throws. Required for 2-step.
  onVerified,         // optional — called after onVerify thành công, parent tự đóng modal
}) {
  const [copied, setCopied] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) { setCode(''); setErr(null); setBusy(false); setVerified(false); return; }
    const handler = (e) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(secretBase32 || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {}
  }

  async function requestClose() {
    if (!verified) {
      const ok = await showConfirm({
        title: 'Đóng mà chưa xác nhận?',
        message:
          'Bạn chưa xác nhận mã 6 số.\n\n' +
          'Nếu đóng bây giờ, secret MỚI sẽ KHÔNG được lưu — TOTP cũ (nếu có) vẫn hoạt động như cũ.\n\n' +
          'Đóng modal?',
        confirmText: 'Đóng',
        dangerous: true,
      });
      if (!ok) return;
    }
    onClose?.();
  }

  async function submit(e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setErr('Mã phải là 6 chữ số'); return; }
    setBusy(true); setErr(null);
    try {
      await onVerify(code);
      setVerified(true);
      // Đợi 1 nhịp cho user thấy trạng thái "thành công" rồi parent tự đóng
      setTimeout(() => { onVerified?.(); }, 600);
    } catch (ex) {
      setErr(ex?.data?.error || ex?.message || 'Xác nhận thất bại');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  const warnColors = warningTone === 'warn'
    ? 'bg-amber-50 border-amber-200 text-amber-800'
    : 'bg-sky-50 border-sky-200 text-sky-800';

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
            {note && <p className="text-sm text-slate-500 mt-1">{note}</p>}
          </div>
          <button onClick={requestClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        {!verified ? (
          <>
            <div className={`text-xs rounded-lg border px-3 py-2 ${warnColors}`}>
              Bước 1: Quét QR vào Authenticator. Bước 2: Nhập mã 6 số hiện ra để xác nhận.
              Nếu không xác nhận, secret mới sẽ KHÔNG được lưu.
            </div>

            {qrPng && (
              <div className="flex justify-center">
                <img src={qrPng} alt="TOTP QR" className="border border-slate-200 rounded" />
              </div>
            )}

            <div>
              <div className="text-xs text-slate-500 mb-1">Chuỗi bí mật (Base32):</div>
              <div className="flex gap-2">
                <code className="flex-1 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono break-all select-all">
                  {secretBase32}
                </code>
                <button onClick={copy} className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-medium">
                  {copied ? '✓ Đã copy' : 'Copy'}
                </button>
              </div>
            </div>

            {otpauthUri && (
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer">otpauth URI (xem thêm)</summary>
                <code className="block mt-2 px-3 py-2 rounded bg-slate-50 border border-slate-200 break-all">
                  {otpauthUri}
                </code>
              </details>
            )}

            <form onSubmit={submit} className="space-y-2">
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">Mã 6 số từ Authenticator</span>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  autoFocus
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none text-center text-2xl font-mono tracking-widest"
                />
              </label>
              {err && <div className="text-xs text-rose-700 bg-rose-50 rounded px-2 py-1.5 border border-rose-200">{err}</div>}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={requestClose}
                  className="flex-1 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium"
                >
                  Huỷ
                </button>
                <button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  className="flex-[2] py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-medium"
                >
                  {busy ? 'Đang xác nhận…' : 'Xác nhận & Lưu'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="py-8 text-center space-y-3">
            <div className="text-5xl">✅</div>
            <div className="text-lg font-medium text-emerald-700">Đã lưu thành công</div>
            <div className="text-sm text-slate-500">Secret mới đã được commit vào DB.</div>
          </div>
        )}
      </div>
    </div>
  );
}
